import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components/macro";
import _ from "lodash";
import { useLocation, useHistory } from "react-router-dom";
import {
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Checkbox,
    TablePagination,
} from "@material-ui/core";
import { Search, Delete, Description, BarChart, Warning } from "@material-ui/icons";
import { enqueueSnackbar } from "notistack";
import Loading from "../components/Loading";
import MyRadonInterface from "../../integration/internal/MyRadon/MyRadonInterface";
import moment from "moment";
import { createMuiTheme } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import AnalysisInProgressDialog from "../../components/AnalysisInProgressDialog";
import ReportDeleteConfirmDialog from "../../components/ReportDeleteConfirmDialog";
import ReportDeleteConfirmDialogMobile from "../../components/ReportDeleteConfirmDialogMobile";
import {
    FAILURE,
    NO_ELEMENTS_MATCHED,
    SUCCESS,
} from "../../constants/resultCodes";
import * as types from "../../constants";

const theme = createMuiTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    typography: {
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        h6: {
            fontSize: "16px",
        },
    },
});

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 24px 32px;
  font-family: 'Nunito', 'Inter', sans-serif;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 16px 0 !important;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 16px;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

const Title = styled(Typography)`
  font-family: 'Nunito', sans-serif !important;
  font-weight: 700 !important;
  font-size: 22px !important;
  color: #1e293b;
  margin-bottom: 0 !important;
`;

const Subtitle = styled(Typography)`
  font-family: 'Nunito', sans-serif !important;
  font-size: 13px !important;
  color: #64748b;
  line-height: 1.4 !important;
  margin-top: 4px !important;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-grow: 1;
  max-width: 650px;
  min-width: 280px;

  @media (max-width: 600px) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  color: #3b82f6;
  display: flex;
  align-items: center;
  pointer-events: none;

  @media (max-width: 600px) {
    left: 14px;
    color: #94a3b8;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: 1.5px solid #3b82f6;
  padding: 0 16px 0 46px;
  outline: none;
  font-size: 14px;
  font-family: 'Nunito', sans-serif;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #1d4ed8;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  @media (max-width: 600px) {
    height: 40px;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    padding: 0 16px 0 42px;
    font-size: 13px;

    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  color: ${(props) => (props.disabled ? "#cbd5e1" : "#D14343")};
  font-weight: 500;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    color: ${(props) => (props.disabled ? "#cbd5e1" : "#ef4444")};
    background-color: ${(props) =>
        props.disabled ? "transparent" : "#fef2f2"};
  }

  @media (max-width: 600px) {
    padding: 6px;
    &:hover {
      background-color: transparent;
    }
  }
`;

const StyledCard = styled(Card)`
  background: #ffffff;
  border-radius: 12px !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;

  @media (max-width: 600px) {
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    box-shadow: none !important;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;

  @media (max-width: 600px) {
    min-width: 580px;
  }
`;

const StyledTableHead = styled(TableHead)`
  background-color: #ffffff;
`;

const StyledHeaderCell = styled(TableCell)`
  font-family: 'Nunito', sans-serif !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  color: #8a94a6 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px 8px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  white-space: nowrap !important;
  text-align: center !important;
  box-sizing: border-box !important;

  ${(props) =>
        props.$sticky &&
        css`
      position: sticky !important;
      left: ${props.$stickyLeft || 0}px !important;
      z-index: 3 !important;
      background-color: #ffffff !important;
    `}

  & .MuiTableSortLabel-root {
    display: inline-flex !important;
    justify-content: center !important;
    align-items: center !important;
    margin: 0 auto !important;
    text-align: center !important;
    position: relative !important;
  }

  & .MuiTableSortLabel-icon {
    opacity: 0 !important;
    position: absolute !important;
    left: calc(100% + 2px) !important;
    top: 50% !important;
    margin-top: -7px !important;
    font-size: 14px !important;
    flex-shrink: 0 !important;
    pointer-events: auto !important;
    transition: opacity 0.15s ease, transform 0.15s ease !important;
  }

  & .MuiTableSortLabel-active .MuiTableSortLabel-icon,
  & .Mui-active .MuiTableSortLabel-icon {
    opacity: 1 !important;
  }

  @media (max-width: 600px) {
    padding: 12px 4px !important;
    font-size: 10px !important;
    white-space: nowrap !important;
    text-align: center !important;

    & .MuiTableSortLabel-icon {
      font-size: 12px !important;
      left: calc(100% + 1px) !important;
      margin-top: -6px !important;
    }

    ${(props) =>
        props.hideMobile &&
        css`
        display: none !important;
      `}
  }

  ${(props) =>
        props.$divider &&
        css`
      border-right: 1px solid #e2e8f0 !important;
    `}
`;

const StyledRow = styled(TableRow)`
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  background-color: #ffffff;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${(props) => (props.$disabled ? "#ffffff" : "#f8fafc")} !important;
  }

  &.Mui-selected,
  &.Mui-selected:hover {
    background-color: #f1f5f9 !important;
  }
`;

const StyledCell = styled(TableCell)`
  font-family: 'Nunito', sans-serif !important;
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  white-space: nowrap;
  box-sizing: border-box !important;

  ${(props) =>
        props.$sticky &&
        css`
      position: sticky !important;
      left: ${props.$stickyLeft || 0}px !important;
      z-index: 2 !important;
      background-color: inherit !important;
    `}

  ${(props) =>
        props.$divider &&
        css`
      border-right: 1px solid #e2e8f0 !important;
    `}
`;

const StyledTablePagination = styled(TablePagination)`
  font-family: 'Nunito', sans-serif !important;
  color: #334155 !important;

  & .MuiTablePagination-caption,
  & .MuiTablePagination-select,
  & .MuiTablePagination-selectIcon,
  & .MuiTablePagination-input {
    font-family: 'Nunito', sans-serif !important;
    font-size: 13px !important;
    color: #334155 !important;
  }

  @media (max-width: 600px) {
    & .MuiTablePagination-caption,
    & .MuiTablePagination-select,
    & .MuiTablePagination-input {
      font-size: 12px !important;
    }
  }
`;

const SessionNameText = styled.span`
  font-weight: 500;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
`;

const RnAvgWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-family: 'Nunito', sans-serif;
  font-weight: 500;
  line-height: 1.2;
  border: 1px solid transparent;
  white-space: nowrap;

  svg {
    font-size: 13px;
  }

  @media (max-width: 600px) {
    padding: 3px 6px;
    font-size: 10px;
    gap: 2px;

    svg {
      font-size: 11px;
    }
  }

  ${(props) =>
        props.type === "Analysis" &&
        css`
      background-color: #f0f3ff;
      color: #7c7bad;
      border-color: #e2e6ff;
    `}

  ${(props) =>
        props.type === "Report" &&
        css`
      background-color: #f0f3ff;
      color: #7c7bad;
      border-color: #e2e6ff;
    `}
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;

  @media (max-width: 600px) {
    padding: 4px 10px;
    font-size: 11px;
  }
`;

const ReportListEcosense = () => {
    const location = useLocation();
    const history = useHistory();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: "requested",
        direction: "desc",
    });
    const [open, setOpen] = useState(false);
    const [span, setSpan] = useState(null);
    const [collected, setCollected] = useState(null);
    const [serialNumber, setSerialNumber] = useState(null);
    const [selectedReportId, setSelectedReportId] = useState(null);

    // Redesign state
    const [searchQuery, setSearchQuery] = useState("");
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    let { email, reportId } = location.state || {};
    email = email || localStorage.getItem("0xaccountId");

    const handleClose = () => {
        setOpen(false);
        setSpan(null);
        setCollected(null);
        setSerialNumber(null);
        setSelectedReportId(null);
    };

    const handleCancel = async () => {
        if (!serialNumber || !selectedReportId) {
            enqueueSnackbar("Missing serial number or report ID", {
                variant: "error",
            });
            return;
        }

        try {
            setLoading(true);
            const res = await MyRadonInterface.cancelAnalysis(
                serialNumber,
                selectedReportId
            );
            if (res.resultcode === SUCCESS) {
                enqueueSnackbar("Analysis cancelled successfully", {
                    variant: "success",
                });
                // Refresh the table data after cancellation
                const updatedData = await MyRadonInterface.getMyRadonList(email);
                setTableData(
                    _.orderBy(
                        _.get(updatedData, "data", []),
                        [sortConfig.key],
                        [sortConfig.direction]
                    )
                );
            } else {
                throw new Error("Cancellation failed");
            }
        } catch (error) {
            console.error("Error cancelling radon analysis:", error);
            enqueueSnackbar("Error cancelling radon analysis", {
                variant: "eNRPP-Listed Devicerror",
            });
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    const normalizeReportItem = (item, defaultType) => {
        if (!item) return {};
        const itemType = item.rpt_type;
        const rptId = item.id || item.sn_report_start;
        const status = item.status;
        const spanVal = item.duration || "--";

        const getTimestampInSec = (val) => {
            if (!val) return 0;
            if (typeof val === "number") {
                return val > 1e11 ? Math.floor(val / 1000) : val;
            }
            const num = Number(val);
            if (!isNaN(num) && num > 0) {
                return num > 1e11 ? Math.floor(num / 1000) : num;
            }
            const parsed = new Date(val).getTime();
            return isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
        };

        let rawRequested = item.created;
        const requestedTs = getTimestampInSec(rawRequested) || Math.floor(Date.now() / 1000);

        let radonAvgVal = item.avg_radon;
        if (radonAvgVal !== undefined && radonAvgVal !== null) {
            if (item.countryCode === "CA") {
                radonAvgVal = Math.round(Number(radonAvgVal));
            } else {
                radonAvgVal = parseFloat((Number(radonAvgVal) / 37).toFixed(1));
            }
        }

        return {
            ...item,
            rpt_id: rptId,
            sn_report_start: item.sn_report_start || rptId,
            device_name: item.device_name || item.session_name,
            requested: requestedTs,
            processed: status,
            sn: item.sn || item.serial_number || "DEMO_SERIAL",
            span: spanVal,
            radonAvg: radonAvgVal,
            type: itemType,
        };
    };

    useEffect(() => {
        const loadReportList = async () => {
            try {
                setLoading(true);
                const res = await MyRadonInterface.getMyReportList(email);

                const resData = res?.data || res?.result || res || {};
                let combinedList = [];

                if (resData.insight_reports || resData.reports) {
                    const insightItems = (resData.insight_reports || []).map(item => normalizeReportItem(item, "Analysis"));
                    const reportItems = (resData.reports || []).map(item => normalizeReportItem(item, "Report"));
                    combinedList = [...insightItems, ...reportItems];
                } else if (Array.isArray(resData)) {
                    combinedList = resData.map(item => normalizeReportItem(item, item.type || "Report"));
                }

                setTableData(_.orderBy(combinedList, ["requested"], ["desc"]));
            } catch (error) {
                console.error("Error fetching report list from union API:", error);
            } finally {
                setLoading(false);
            }
        };
        loadReportList();
    }, [email]);

    const handleSort = (key) => {
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction });
        setTableData((prevData) =>
            _.orderBy(prevData, [(row) => getSortValue(row, key)], [direction])
        );
    };

    const isCompletedStatus = (row) => {
        return String(row.status || "").toUpperCase() === "COMPLETED";
    };

    const handleRowClick = (row) => {
        if (!isCompletedStatus(row)) return;

        const { rpt_id } = row;
        const typeVal = getReportType(row);
        if (typeVal === "Analysis") {
            history.push("/myreport", { email, reportId: rpt_id });
        } else {
            history.push("/radon", { email, reportId: rpt_id });
        }
    };

    // Helper functions for displaying columns
    const getRnAvg = (row) => {
        if (row.radonAvg !== undefined && row.radonAvg !== null)
            return parseFloat(row.radonAvg);
        if (row.radon_avg !== undefined && row.radon_avg !== null)
            return parseFloat(row.radon_avg);
        if (
            row.analysis_radon &&
            row.analysis_radon.avg !== undefined &&
            row.analysis_radon.avg !== null
        ) {
            const avgBq = row.analysis_radon.avg;
            return row.countryCode === "CA"
                ? Math.round(avgBq)
                : parseFloat((avgBq * 0.027).toFixed(1));
        }
        return null;
    };

    const getDotColor = (avg, countryCode) => {
        if (avg === null || avg === undefined || isNaN(avg)) return "#e0e0e0";
        if (countryCode === "CA") {
            if (avg < 100) return "#4caf50";
            if (avg < 200) return "#ff9800";
            return "#f44336";
        }
        if (avg < 2.7) return "#4caf50";
        if (avg <= 3.9) return "#ff9800";
        return "#f44336";
    };

    const getReportType = (row) => {
        if (row.type) {
            const t = String(row.type).toUpperCase();
            if (t.includes("ARP")) return "Report";
            if (t.includes("MRR")) return "Analysis";
            if (t.includes("ARA")) return "Analysis";
            if (t.includes("FRA")) return "Analysis";
        }
        return "Report";
    };

    const getDurationStr = (span) => {
        if (!span) return "--";
        const str = String(span).trim();
        if (!str || str === "null" || str === "undefined") return "--";
        if (/[a-zA-Z]/.test(str)) return str;
        return `${str}`;
    };

    const getStatusConfig = (status) => {
        const s = String(status || "").trim().toUpperCase();
        switch (s) {
            case "PENDING":
                return {
                    label: "In Progress",
                    showIcon: false,
                    bg: "#e0f2fe",
                    color: "#0284c7",
                };
            case "AT_RISK":
                return {
                    label: "In Progress",
                    showIcon: true,
                    bg: "#ffedd5",
                    color: "#ea580c",
                };
            case "HIGH_RISK":
                return {
                    label: "In Progress",
                    showIcon: true,
                    bg: "#fee2e2",
                    color: "#dc2626",
                };
            case "CANCELED":
            case "CANCELLED":
                return {
                    label: "Canceled",
                    showIcon: false,
                    bg: "#f1f5f9",
                    color: "#475569",
                };
            case "INSUFFICIENT":
            case "FAILED":
                return {
                    label: "Unavailable",
                    showIcon: false,
                    bg: "#f1f5f9",
                    color: "#475569",
                };
            case "COMPLETED":
            case "READY":
                return {
                    label: "Completed",
                    showIcon: false,
                    bg: "#dcfce7",
                    color: "#16a34a",
                };
            default:
                return {
                    label: status || "N/A",
                    showIcon: false,
                    bg: "#f1f5f9",
                    color: "#475569",
                };
        }
    };

    const getStatusDisplay = (row) => {
        return getStatusConfig(row?.status).label;
    };

    const renderStatusBadge = (status) => {
        const config = getStatusConfig(status);
        return (
            <StatusBadge style={{ backgroundColor: config.bg, color: config.color }}>
                {config.showIcon && <Warning style={{ fontSize: 15, color: config.color, marginRight: 4 }} />}
                {config.label}
            </StatusBadge>
        );
    };

    const getSortValue = (row, key) => {
        if (key === "device_name") return row.device_name || "";
        if (key === "requested") return row.requested || 0;
        if (key === "processed") return getStatusDisplay(row);
        if (key === "rnAvg") return getRnAvg(row) ?? -1;
        if (key === "type") return getReportType(row);
        if (key === "duration") return parseInt(row.span, 10) || 0;
        return "";
    };

    // Selection handlers
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = filteredData.map((n) => n.rpt_id);
            setSelected(newSelecteds);
        } else {
            setSelected([]);
        }
    };

    const handleSelectOne = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleDeleteSelected = () => {
        if (selected.length === 0) return;
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (selected.length === 0) return;
        setLoading(true);
        try {
            const selectedRows = tableData.filter((row) => selected.includes(row.rpt_id));
            const items = selectedRows.map((row) => {
                let rptType = row.rpt_type;
                if (!rptType) {
                    rptType = getReportType(row) === "Report" ? "ARP" : "ARA";
                }
                const itemId = rptType === "ARP"
                    ? (row.sn_report_start || row.rpt_id || row.id)
                    : (row.id || row.rpt_id);
                return {
                    id: itemId,
                    rpt_type: rptType,
                };
            });

            const res = await MyRadonInterface.deleteReports(items);

            if (res && (res.resultcode === 200 || res.resultcode === SUCCESS || res.status === 200)) {
                const deletedList = res.data?.deleted || [];
                const deletedIdSet = new Set(deletedList.map((d) => String(d.id)));

                if (deletedList.length > 0) {
                    setTableData((prev) =>
                        prev.filter((row) => {
                            const id1 = String(row.rpt_id || "");
                            const id2 = String(row.sn_report_start || "");
                            const id3 = String(row.id || "");
                            const isDeleted = deletedIdSet.has(id1) || deletedIdSet.has(id2) || deletedIdSet.has(id3);
                            return !isDeleted;
                        })
                    );
                    enqueueSnackbar(`Successfully deleted ${deletedList.length} report(s).`, {
                        variant: "success",
                    });
                } else {
                    enqueueSnackbar(`Failed to delete report(s).`, {
                        variant: "error",
                    });
                }
            } else {
                enqueueSnackbar(`Failed to delete report(s).`, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("Error deleting report(s):", error);
            enqueueSnackbar("Error deleting report(s).", {
                variant: "error",
            });
        } finally {
            setSelected([]);
            setOpenDeleteDialog(false);
            setLoading(false);
        }
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter query reset page
    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const filteredData = tableData.filter((row) => {
        const sName = (row.device_name || "").toLowerCase();
        const type = getReportType(row).toLowerCase();
        const query = searchQuery.toLowerCase();
        return sName.includes(query) || type.includes(query);
    });

    const paginatedData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
    const allSelectedOnPage =
        filteredData.length > 0 &&
        filteredData.every((n) => selected.includes(n.rpt_id));
    const someSelectedOnPage =
        filteredData.some((n) => selected.includes(n.rpt_id)) && !allSelectedOnPage;

    if (loading) return <Loading />;

    return (
        <Container>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <AnalysisInProgressDialog
                    handleClose={handleClose}
                    handleCancel={handleCancel}
                    reportId={selectedReportId}
                    span={span}
                    collected={collected}
                />
            </Dialog>

            {screenWidth <= 600 ? (
                <ReportDeleteConfirmDialogMobile
                    open={openDeleteDialog}
                    onClose={() => setOpenDeleteDialog(false)}
                    onConfirm={handleConfirmDelete}
                    selectedCount={selected.length}
                />
            ) : (
                <ReportDeleteConfirmDialog
                    open={openDeleteDialog}
                    onClose={() => setOpenDeleteDialog(false)}
                    onConfirm={handleConfirmDelete}
                    selectedCount={selected.length}
                />
            )}

            {screenWidth <= 600 ? (
                // Mobile Header
                <HeaderSection>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Title variant="h4">My Reports</Title>
                        <DeleteButton
                            disabled={selected.length === 0}
                            onClick={handleDeleteSelected}
                        >
                            <Delete style={{ fontSize: 20 }} />
                        </DeleteButton>
                    </div>
                    <Subtitle variant="subtitle1">
                        Analysis and radon reports from your EcoQube devices.
                    </Subtitle>
                </HeaderSection>
            ) : (
                // Original Desktop Header
                <HeaderSection>
                    <Title variant="h4">My Reports</Title>
                    <Subtitle variant="subtitle1">
                        Analysis and Radon Reports from your EcoQube devices.
                    </Subtitle>
                </HeaderSection>
            )}

            <ActionBar>
                <SearchWrapper>
                    <SearchIconWrapper>
                        <Search style={{ fontSize: 20 }} />
                    </SearchIconWrapper>
                    <SearchInput
                        type="text"
                        placeholder="Search by session or type"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </SearchWrapper>

                {screenWidth > 600 && (
                    <DeleteButton
                        disabled={selected.length === 0}
                        onClick={handleDeleteSelected}
                    >
                        <Delete style={{ fontSize: 18 }} />
                        Delete
                    </DeleteButton>
                )}
            </ActionBar>

            {tableData.length > 0 ? (
                <StyledCard>
                    <CardContent style={{ padding: 0 }}>
                        <TableContainer>
                            <StyledTable>
                                <StyledTableHead>
                                    {screenWidth <= 600 ? (
                                        // Mobile Table Head
                                        <TableRow>
                                            <StyledHeaderCell align="center" $divider $sticky $stickyLeft={0} style={{ maxWidth: 130, minWidth: 110 }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "device_name"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("device_name")}
                                                >
                                                    SESSION NAME
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell align="center" style={{ width: "12%" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "rnAvg"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("rnAvg")}
                                                >
                                                    RN AVG
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell align="center" style={{ width: "14%" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "type"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("type")}
                                                >
                                                    TYPE
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell align="center" style={{ width: "12%" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "duration"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("duration")}
                                                >
                                                    DURATION
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell align="center" style={{ width: "18%" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "processed"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("processed")}
                                                >
                                                    STATUS
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell align="center" style={{ width: "18%" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "requested"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("requested")}
                                                >
                                                    GENERATED
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                        </TableRow>
                                    ) : (
                                        // Original Desktop Table Head
                                        <TableRow>
                                            <StyledHeaderCell $sticky $stickyLeft={0} style={{ width: 52, minWidth: 52, maxWidth: 52 }} />
                                            <StyledHeaderCell $divider $sticky $stickyLeft={52} style={{ width: "20%" }}>
                                                <TableSortLabel
                                                    active={sortConfig.key === "device_name"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("device_name")}
                                                >
                                                    Session Name
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell style={{ width: "16%" }} align="center">
                                                <TableSortLabel
                                                    active={sortConfig.key === "rnAvg"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("rnAvg")}
                                                >
                                                    RN AVG
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell style={{ width: "16%" }} align="center">
                                                <TableSortLabel
                                                    active={sortConfig.key === "type"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("type")}
                                                >
                                                    Type
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell style={{ width: "14%" }} align="center">
                                                <TableSortLabel
                                                    active={sortConfig.key === "duration"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("duration")}
                                                >
                                                    Duration
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell style={{ width: "14%" }} align="center">
                                                <TableSortLabel
                                                    active={sortConfig.key === "processed"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("processed")}
                                                >
                                                    Status
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                            <StyledHeaderCell style={{ width: "12%" }} align="center">
                                                <TableSortLabel
                                                    active={sortConfig.key === "requested"}
                                                    direction={sortConfig.direction}
                                                    onClick={() => handleSort("requested")}
                                                >
                                                    Requested
                                                </TableSortLabel>
                                            </StyledHeaderCell>
                                        </TableRow>
                                    )}
                                </StyledTableHead>
                                <TableBody>
                                    {paginatedData.length > 0 ? (
                                        paginatedData.map((row) => {
                                            const {
                                                rpt_id,
                                                device_name,
                                                requested,
                                                processed,
                                                sn,
                                                span,
                                                collected,
                                            } = row;
                                            const isSelected = selected.includes(rpt_id);
                                            const avgValue = getRnAvg(row);
                                            const typeVal = getReportType(row);
                                            const durationStr = getDurationStr(span);

                                            const isCompleted = isCompletedStatus(row);

                                            if (screenWidth <= 600) {
                                                // Mobile Row
                                                return (
                                                    <StyledRow
                                                        key={rpt_id}
                                                        hover={isCompleted}
                                                        onClick={() => isCompleted && handleRowClick(row)}
                                                        selected={isSelected}
                                                        $disabled={!isCompleted}
                                                    >
                                                        <StyledCell $divider $sticky $stickyLeft={0} style={{ maxWidth: 130, minWidth: 110 }}>
                                                            <div style={{ display: "flex", alignItems: "center", minWidth: 0, overflow: "hidden" }}>
                                                                <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onChange={() => handleSelectOne(rpt_id)}
                                                                        color="primary"
                                                                        style={{ padding: "0 4px 0 0" }}
                                                                    />
                                                                </div>
                                                                <SessionNameText title={device_name || "--"}>{device_name || "--"}</SessionNameText>
                                                            </div>
                                                        </StyledCell>
                                                        <StyledCell align="center">
                                                            <RnAvgWrapper style={{ justifyContent: "center" }}>
                                                                <StatusDot color={getDotColor(avgValue, row.countryCode)} />
                                                                <span>
                                                                    {avgValue !== null
                                                                        ? (row.countryCode === "CA" ? avgValue.toFixed(0) : avgValue.toFixed(1))
                                                                        : "--"}
                                                                </span>
                                                            </RnAvgWrapper>
                                                        </StyledCell>
                                                        <StyledCell align="center">
                                                            <Badge type={typeVal}>
                                                                {typeVal === "Analysis" ? (
                                                                    <BarChart />
                                                                ) : (
                                                                    <Description />
                                                                )}
                                                                {typeVal === "Analysis" ? "Analys." : typeVal}
                                                            </Badge>
                                                        </StyledCell>
                                                        <StyledCell align="center">{durationStr}</StyledCell>
                                                        <StyledCell align="center">
                                                            {renderStatusBadge(row.status)}
                                                        </StyledCell>
                                                        <StyledCell align="center">
                                                            {moment.unix(requested).format("MM/DD")}
                                                        </StyledCell>
                                                    </StyledRow>
                                                );
                                            }

                                            // Original Desktop Row
                                            return (
                                                <StyledRow
                                                    key={rpt_id}
                                                    hover={isCompleted}
                                                    onClick={() => handleRowClick(row)}
                                                    selected={isSelected}
                                                    $disabled={!isCompleted}
                                                >
                                                    <StyledCell onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={() => handleSelectOne(rpt_id)}
                                                            color="primary"
                                                        />
                                                    </StyledCell>
                                                    <StyledCell $divider>
                                                        <SessionNameText>
                                                            {device_name || "--"}
                                                        </SessionNameText>
                                                    </StyledCell>
                                                    <StyledCell align="center">
                                                        <RnAvgWrapper style={{ justifyContent: "center" }}>
                                                            <StatusDot color={getDotColor(avgValue, row.countryCode)} />
                                                            <span>
                                                                {avgValue !== null
                                                                    ? (row.countryCode === "CA" ? avgValue.toFixed(0) : avgValue.toFixed(1))
                                                                    : "--"}
                                                            </span>
                                                        </RnAvgWrapper>
                                                    </StyledCell>
                                                    <StyledCell align="center">
                                                        <Badge type={typeVal}>
                                                            {typeVal === "Analysis" ? (
                                                                <BarChart />
                                                            ) : (
                                                                <Description />
                                                            )}
                                                            {typeVal}
                                                        </Badge>
                                                    </StyledCell>
                                                    <StyledCell align="center">{durationStr}</StyledCell>
                                                    <StyledCell align="center">
                                                        {renderStatusBadge(row.status)}
                                                    </StyledCell>
                                                    <StyledCell align="center">
                                                        {moment.unix(requested).format("MM/DD/YY")}
                                                    </StyledCell>
                                                </StyledRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                style={{ padding: "40px 0", color: "#64748b" }}
                                            >
                                                No matching reports found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </StyledTable>
                        </TableContainer>
                        <StyledTablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </CardContent>
                </StyledCard>
            ) : (
                <Card>
                    <CardContent>
                        <Typography
                            variant="h6"
                            style={{
                                textAlign: "center",
                                color: "#808080",
                                fontSize: "13px",
                            }}
                        >
                            {email
                                ? "There are no completed reports generated from this email account."
                                : "There's no passed email. Please try again."}
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Container>
    );
};

export default ReportListEcosense;
